/**
 * The Clients table's Columns button: tick metrics from any source to show
 * one column each (at most 25), and the period they cover.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { PRESET_LABELS } from '../reports/format.ts';
import type { Catalogue } from '../reports/reports-api.ts';
import { columnKey, MAX_COLUMNS, type ClientColumn } from './client-columns.ts';

const PERIODS = [
  'LAST_7_DAYS',
  'LAST_30_DAYS',
  'THIS_MONTH',
  'LAST_MONTH',
  'THIS_QUARTER',
  'LAST_QUARTER',
  'THIS_YEAR',
  'LAST_YEAR',
];

export function ClientPeriodSelect(props: {
  preset: string;
  onChange: (preset: string) => void;
}): ReactNode {
  return (
    <label className="kp-inline">
      <span className="sr-only">Period</span>
      <select value={props.preset} onChange={(event) => props.onChange(event.target.value)}>
        {PERIODS.map((preset) => (
          <option key={preset} value={preset}>
            {PRESET_LABELS[preset] ?? preset}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ClientColumnsPicker(props: {
  catalogue: Catalogue;
  columns: ClientColumn[];
  onChange: (columns: ClientColumn[]) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    const onPointer = (event: PointerEvent) =>
      box.current && !box.current.contains(event.target as Node) && setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);
  const chosen = new Set(props.columns.map(columnKey));
  const isFull = props.columns.length >= MAX_COLUMNS;
  const toggle = (column: ClientColumn) =>
    props.onChange(
      chosen.has(columnKey(column))
        ? props.columns.filter((item) => columnKey(item) !== columnKey(column))
        : [...props.columns, column],
    );
  return (
    <div className="ex-filter" ref={box}>
      <button
        type="button"
        className="btn secondary"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        Columns{props.columns.length > 0 ? ` (${props.columns.length})` : ''}
      </button>
      {open && (
        <div className="ex-pop cc-pop" role="dialog" aria-label="Metric columns">
          <p className="cm-count" role="status">
            {props.columns.length} of {MAX_COLUMNS} columns
          </p>
          {props.catalogue.sources.map((source) => (
            <fieldset key={source.key} className="cc-group">
              <legend>{source.label}</legend>
              {source.metrics.map((metric) => {
                const column = { source: source.key, metric: metric.key };
                const isOn = chosen.has(columnKey(column));
                return (
                  <label key={metric.key} className="kp-check">
                    <input
                      type="checkbox"
                      checked={isOn}
                      disabled={isFull && !isOn}
                      onChange={() => toggle(column)}
                    />
                    {metric.label}
                  </label>
                );
              })}
            </fieldset>
          ))}
        </div>
      )}
    </div>
  );
}
